<script setup lang="ts">
import useWordOfTheDayStore from '../store';
import { storeToRefs } from 'pinia';
import Flower from './Flower.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';
import { useDialogFocus } from '@ilteducation/components';
import { createSearchId, SearchQuery, useSearchContexts } from '@ilteducation/content-search';
import type { VideoMarket, VideoV1 } from '@ilteducation/content-registry-types';
import VideoThumbnail from './video/VideoThumbnail.vue';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import { useDisplay } from 'vuetify';

export type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
  ? ElementType
  : never;

// should probably be a part of @ilteducation/content-registry-types
export interface ITranslated {
  market: 'global' | VideoMarket;
  languageCode: string;
}

export type BookLink = ITranslated & {
  value: {
    bookId: string;
    url?: string;
  }[];
  languageTranslation: string;
};
export declare type Video = Omit<
  VideoV1,
  | 'title'
  | 'description'
  | 'summary'
  | 'slug'
  | 'introfilmsIds'
  | 'introfilmsUids'
  | 'previewImage'
  | 'narrations'
  | 'subtitles'
  | 'bookLinks'
> & {
  title: string;
  description: string;
  summary: string;
  slug: string;
  resourceId: string; // `${videoId}-${realm}` format used in exercise model
  introfilmsIds: NonNullable<VideoV1['introfilmsIds'][VideoMarket]>;
  introfilmsUids: NonNullable<VideoV1['introfilmsUids'][VideoMarket]>;
  previewImage?: string;
  thumbnail?: string;
  narrations: (ITranslated & {
    value: NonNullable<ArrayElement<VideoV1['narrations']>['value']>;
    languageTranslation: string;
  })[];
  subtitles: (ArrayElement<VideoV1['subtitles']> & {
    value: NonNullable<ArrayElement<VideoV1['subtitles']>['value']>;
    languageTranslation: string;
  })[];
  bookLinks: { [categoryId: string]: BookLink[] };
};

function convertVideo(video: VideoV1): Video {
  const cdnDomain = 'https://content.global.prod.ilteducation.com';
  return {
    ...video,
    resourceId: `${video.id}-se`,
    categories: Array.from(new Set(video.categories)),
    title: video.title.find((title) => title.languageCode === 'sv')?.value || '',
    slug: video.slug.se || '',
    description: video.description.find((d) => d.languageCode === 'sv')?.value || '',
    summary: video.summary.find((s) => s.languageCode === 'sv')?.value || '',
    thumbnail: video.previewImage.se
      ? `${cdnDomain}/public/videos/${video.id}/${video.previewImage.se?.fileName}?width=480&aspect_ratio=16:9`
      : undefined,
    previewImage: video.previewImage.se
      ? `${cdnDomain}/public/videos/${video.id}/${video.previewImage.se?.fileName}?width=1280&aspect_ratio=16:9`
      : undefined,
    introfilmsIds: video.introfilmsIds.se || {},
    introfilmsUids: video.introfilmsUids.se || {},
    narrations: [],
    subtitles: [],
    bookLinks: {},
  };
}

const store = useWordOfTheDayStore();
const searchContextsStore = useSearchContexts<VideoV1>('Video');
const { word, wordContext, guesses, keywordOfTheDay } = storeToRefs(store);
const { search: contextSearch } = searchContextsStore;
const { result: contextResult } = storeToRefs(searchContextsStore);

const showModal = ref(false);
const { rememberFocus } = useDialogFocus([showModal]);

const openModal = () => {
  rememberFocus();
  showModal.value = true;
};

const searchQuery: SearchQuery = {
  filter: {
    keywords: keywordOfTheDay.value!.keywordId,
  },
  pagination: { size: 10 },
};
const contextId = createSearchId('Video', searchQuery);
contextSearch(searchQuery, 10);

const videos = computed(() => {
  return (contextResult.value(contextId) as VideoV1[]).map((v) => convertVideo(v)) || [];
});

const { load: loadCategories } = useCategories();
const { load: loadAreas } = useAreas();
loadCategories('immigrant-m');
loadAreas();

const sortedVideos = computed(() => videos.value.flatMap((v) => v.categories.map((cat) => ({ ...v, subject: cat }))));

const restart = () => {
  store.clear();
  store.setGameState('init');
};

const { lgAndUp } = useDisplay();
</script>

<template>
  <div class="the-game">
    <v-row no-gutters class="flex-grow-0">
      <v-col cols="3" class="pt-8">
        <h1 class="title">Dagens ord</h1>
      </v-col>
      <v-col></v-col>
      <v-col class="d-flex justify-end pr-8 pt-4" cols="2">
        <div class="tries-left">
          <Flower height="clamp(70px, 14vh, 150px)" />
          <div>{{ 10 - guesses }} försök kvar</div>
        </div>
      </v-col>
    </v-row>

    <v-row no-gutters class="flex-grow-0">
      <v-col cols="10" class="mx-8">
        <div class="word">
          {{ word }}
          <v-btn icon class="ml-3" v-if="wordContext?.imageUrl" @click="openModal" color="transparent">
            <FontAwesomeIcon size="xl" icon="fa-regular fa-image" />
          </v-btn>
          <v-dialog
            v-model="showModal"
            max-width="min(800px,70dvw)"
            content-class="d-flex justify-center"
            @keydown.esc="showModal = false"
            @click:outside="showModal = false"
          >
            <v-card max-width="100%" min-height="100px" class="pa-4 rounded-lg d-flex justify-center">
              <button @click="showModal = false">
                <v-icon size="x-large" class="close-icon"> mdi-close</v-icon>
              </button>
              <v-img v-if="wordContext?.imageUrl" :src="wordContext.imageUrl" :alt="word" />
            </v-card>
          </v-dialog>
        </div>
        <div v-if="wordContext?.description" class="context">{{ wordContext.description }}</div>
      </v-col>
    </v-row>

    <v-row no-gutters class="related-videos mt-4">
      <v-col class="mx-8">
        <p class="context">Relaterade filmer</p>
        <v-progress-circular v-if="sortedVideos.length === 0" size="64" indeterminate></v-progress-circular>
        <div class="d-flex overflow-x-scroll" v-if="sortedVideos.length > 0">
          <VideoThumbnail
            v-for="video in sortedVideos"
            :key="`${video.id}-${video.subject}`"
            :video="video"
            :subject-id="video.subject || ''"
          >
            {{ video.title }}
          </VideoThumbnail>
        </div>
      </v-col>
    </v-row>

    <v-row no-gutters class="justify-center flex-grow-0 my-4">
      <v-col cols="2" class="d-flex justify-center">
        <v-btn class="action-btn" :height="lgAndUp ? '56px' : '48px'" variant="outlined" @click="restart"
          >Spela igen
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.the-game {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  padding: clamp(0.5rem, 2vh, 1.5rem) clamp(0.5rem, 1.5vw, 1.5rem);
  overflow-y: auto;
}

.related-videos {
  flex: 1 1 0;
  min-height: 0;
}

.title {
  transform: rotate(-10deg);
  transform-origin: top left;
  font-size: 2rem;
  font-weight: bold;
  font-family: 'Chalkduster', sans-serif;
}

.tries-left {
  display: flex;
  flex-direction: column;
  justify-content: end;
  max-width: 150px;
  font-size: 1rem;
}

.word {
  font-size: 2.5rem;

  svg:hover {
    cursor: pointer;
  }
}

.close-icon {
  position: absolute;
  right: 32px;
  top: 32px;
  z-index: 2;
  width: auto;

  &:before {
    border-radius: 50%;
    opacity: 0.75;
  }
}

.context {
  font-size: 1.5rem;
}

img {
  max-width: 100px;
  max-height: 200px;
  align-self: end;
}
</style>
